/**
 * GRiPS Service (GRAutomates Intelligent Personal Support)
 *
 * Frontend service for communicating with the GRiPS chatbot API.
 * Supports both standard request/response and streaming modes.
 *
 * Features:
 * - Standard request/response mode (sendMessage)
 * - Streaming mode for real-time responses (sendMessageStream)
 * - Request cancellation support
 * - Proper error handling
 *
 * GRiPS helps employees get a grip on things — providing instant assistance
 * with GRAutomates features, workflows, and troubleshooting.
 */

import axios, { AxiosError, CancelTokenSource } from 'axios';

// ============================================================================
// Types
// ============================================================================

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isError?: boolean; // Flag to identify error messages for retry functionality
  originalMessage?: string; // Store original message for retry
}

interface ChatStatusResponse {
  success: boolean;
  data?: {
    available: boolean;
    welcomeMessage?: string;
  };
  error?: string;
}

interface ChatMessageResponse {
  success: boolean;
  data?: {
    message: string;
  };
  error?: string;
}

interface ApiErrorResponse {
  error?: string;
  message?: string;
}

/**
 * Stream event types from the SSE endpoint
 */
export interface StreamEvent {
  type: 'start' | 'text' | 'done' | 'error';
  text?: string;
  error?: string;
}

/**
 * Callbacks for streaming responses
 */
export interface StreamCallbacks {
  onStart?: () => void;
  onText?: (text: string) => void;
  onDone?: () => void;
  onError?: (error: string) => void;
}

// ============================================================================
// Configuration
// ============================================================================

export const CHATBOT_CONFIG = {
  MAX_MESSAGE_LENGTH: 2000,
  WARNING_THRESHOLD: 1800, // Show warning when approaching limit
};

// ============================================================================
// Request Cancellation
// ============================================================================

let currentCancelSource: CancelTokenSource | null = null;
let currentAbortController: AbortController | null = null;

/**
 * Cancel any pending chatbot request (both standard and streaming)
 */
export function cancelPendingRequest(): void {
  // Cancel axios requests
  if (currentCancelSource) {
    currentCancelSource.cancel('Request cancelled by user');
    currentCancelSource = null;
  }
  // Cancel streaming requests
  if (currentAbortController) {
    currentAbortController.abort();
    currentAbortController = null;
  }
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Check if the chatbot service is available
 */
export async function getChatbotStatus(): Promise<{
  available: boolean;
  welcomeMessage?: string;
}> {
  try {
    const { data } = await axios.get<ChatStatusResponse>('/api/chatbot/status');

    if (data.success && data.data) {
      return {
        available: data.data.available,
        welcomeMessage: data.data.welcomeMessage,
      };
    }

    return { available: false };
  } catch {
    return { available: false };
  }
}

/**
 * Send a message to the chatbot
 * Supports request cancellation via cancelPendingRequest()
 */
export async function sendMessage(
  message: string,
  history: ChatMessage[]
): Promise<{
  success: boolean;
  message?: string;
  error?: string;
  cancelled?: boolean;
}> {
  // Cancel any existing request
  cancelPendingRequest();

  // Create new cancel token
  currentCancelSource = axios.CancelToken.source();

  try {
    // Convert history to the format expected by API (without id, timestamp, and error flags)
    const apiHistory = history
      .filter(msg => !msg.isError) // Exclude error messages from history
      .map(msg => ({
        role: msg.role,
        content: msg.content,
      }));

    const { data } = await axios.post<ChatMessageResponse>(
      '/api/chatbot/message',
      {
        message,
        history: apiHistory,
      },
      {
        cancelToken: currentCancelSource.token,
      }
    );

    // Clear cancel source on success
    currentCancelSource = null;

    if (data.success && data.data) {
      return {
        success: true,
        message: data.data.message,
      };
    }

    return {
      success: false,
      error: data.error || 'Failed to get response',
    };
  } catch (error: unknown) {
    // Clear cancel source
    currentCancelSource = null;

    // Check if request was cancelled
    if (axios.isCancel(error)) {
      return {
        success: false,
        cancelled: true,
        error: 'Request was cancelled',
      };
    }

    // Handle Axios errors with proper typing
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;

      // Network error (no response)
      if (!axiosError.response) {
        return {
          success: false,
          error: 'Network error. Please check your connection and try again.',
        };
      }

      // Server responded with error
      const serverError = axiosError.response.data?.error || axiosError.response.data?.message;
      if (serverError) {
        return {
          success: false,
          error: serverError,
        };
      }

      // HTTP status-based errors
      const status = axiosError.response.status;
      if (status === 429) {
        return {
          success: false,
          error: 'Too many requests. Please wait a moment before trying again.',
        };
      }
      if (status === 401 || status === 403) {
        return {
          success: false,
          error: 'Session expired. Please refresh the page and try again.',
        };
      }
      if (status >= 500) {
        return {
          success: false,
          error: 'Server error. Please try again later.',
        };
      }
    }

    // Generic error fallback
    return {
      success: false,
      error: 'Failed to send message. Please try again.',
    };
  }
}

/**
 * Send a message to the chatbot with streaming response
 * Uses Server-Sent Events for real-time response streaming
 *
 * @param message - The user's message
 * @param history - Previous conversation messages
 * @param callbacks - Callbacks for stream events
 * @returns Promise that resolves when stream completes
 */
export async function sendMessageStream(
  message: string,
  history: ChatMessage[],
  callbacks: StreamCallbacks
): Promise<{ success: boolean; cancelled?: boolean; error?: string }> {
  // Cancel any existing request
  cancelPendingRequest();

  // Create new abort controller for this request
  currentAbortController = new AbortController();

  try {
    // Get auth token from localStorage user object (same pattern as App.tsx)
    const userJson = localStorage.getItem('user');
    const token = userJson ? JSON.parse(userJson).token : null;
    if (!token) {
      callbacks.onError?.('Session expired. Please refresh the page and try again.');
      return { success: false, error: 'No auth token' };
    }

    // Convert history to API format
    const apiHistory = history
      .filter(msg => !msg.isError)
      .map(msg => ({
        role: msg.role,
        content: msg.content,
      }));

    // Determine API base URL
    const apiBase = process.env.REACT_APP_API_BASE || '';

    const response = await fetch(`${apiBase}/api/chatbot/message/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        message,
        history: apiHistory,
      }),
      signal: currentAbortController.signal,
    });

    // Check for non-200 responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = (errorData as ApiErrorResponse).error || `Server error: ${response.status}`;
      callbacks.onError?.(errorMessage);
      return { success: false, error: errorMessage };
    }

    // Process the SSE stream
    const reader = response.body?.getReader();
    if (!reader) {
      callbacks.onError?.('Failed to read response stream');
      return { success: false, error: 'No reader' };
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      // Decode the chunk and add to buffer
      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE messages (each ends with \n\n)
      const messages = buffer.split('\n\n');
      buffer = messages.pop() || ''; // Keep incomplete message in buffer

      for (const msg of messages) {
        if (!msg.trim()) continue;

        // Parse SSE format: "data: {...}"
        const dataMatch = msg.match(/^data:\s*(.+)$/m);
        if (!dataMatch) continue;

        try {
          const event: StreamEvent = JSON.parse(dataMatch[1]);

          switch (event.type) {
            case 'start':
              callbacks.onStart?.();
              break;
            case 'text':
              if (event.text) {
                callbacks.onText?.(event.text);
              }
              break;
            case 'done':
              callbacks.onDone?.();
              break;
            case 'error':
              callbacks.onError?.(event.error || 'An error occurred');
              return { success: false, error: event.error };
          }
        } catch {
          // Ignore JSON parse errors for malformed events
          console.warn('[Chatbot] Failed to parse SSE event:', msg);
        }
      }
    }

    // Clear abort controller on success
    currentAbortController = null;

    return { success: true };
  } catch (error: unknown) {
    // Clear abort controller
    currentAbortController = null;

    // Check if request was cancelled/aborted
    if (error instanceof Error && error.name === 'AbortError') {
      return { success: false, cancelled: true };
    }

    // Network error
    const errorMessage = error instanceof Error ? error.message : 'Network error';
    callbacks.onError?.('Network error. Please check your connection and try again.');
    return { success: false, error: errorMessage };
  }
}

/**
 * Generate a unique message ID
 */
export function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Simple HTML entity encoding to prevent XSS
 * Note: React already escapes content, but this adds extra safety for edge cases
 */
export function sanitizeContent(content: string): string {
  return content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Parse markdown-like formatting for display
 * Converts basic markdown to safe text formatting hints
 */
export function parseMarkdownBasic(content: string): {
  type: 'text' | 'bold' | 'code' | 'list-item';
  content: string;
}[] {
  // For now, return as plain text
  // This can be enhanced to support markdown parsing if needed
  return [{ type: 'text', content }];
}

export default {
  getChatbotStatus,
  sendMessage,
  sendMessageStream,
  generateMessageId,
  cancelPendingRequest,
  sanitizeContent,
  CHATBOT_CONFIG,
};
